import React, { useEffect, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPhone,
  faMicrophone,
  faMicrophoneSlash,
  faVideoSlash,
  faVideo,
  faPaperclip,
} from "@fortawesome/free-solid-svg-icons";
import { ref, update } from "firebase/database";
import { database } from "../firebase/firebaseConfig";
import AgoraRTC from "agora-rtc-sdk-ng";
import "./video-call.css";
import { Modal, Button } from "react-bootstrap";

const VideoCall = () => {
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const localAudioRef = useRef(null);
  const ringingRef = useRef(new Audio("/ringtone.mp3"));
  const ringTimeoutRef = useRef(null);
  const [client, setClient] = useState(null);
  const [ringing, setRinging] = useState(true);
  const [audioMuted, setAudioMuted] = useState(false);
  const [videoMuted, setVideoMuted] = useState(false);
  const [cname, setDoctorEmail] = useState(null);
  const [appointmentId, setDoctorAppointmentId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [remoteUserPublished, setRemoteUserPublished] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if (location.state && location.state.email) {
      setDoctorEmail(location.state.email);
      setDoctorAppointmentId(location.state.appointment);
    } else {
      console.error("Doctor email not available in location state.");
    }
  }, [location]);

  const session = JSON.parse(sessionStorage.getItem("user"));
  const senderEmail = session.payLoad.user.Email;

  const email = cname ? cname.replace(/\./g, "") : null;

  const navigateToFeedback = () => {
    const doctorId = location.state?.doctorId;
    const callFrom = location.state?.fromCall;

    // Navigate to feedback page and replace the current history entry
    navigate("/feedback", {
      replace: true, // ✅ This prevents back navigation to the current page
      state: {
        doctorId,
        callFrom,
      },
    });
  };

  const clickVideoCall = useCallback(async () => {
    // Stop local video and audio tracks
    if (localVideoRef.current) {
      const localVideoTrack =
        localVideoRef.current.srcObject.getVideoTracks()[0];
      if (localVideoTrack) {
        localVideoTrack.stop();
      }
    }

    if (localAudioRef.current) {
      const localAudioTrack =
        localAudioRef.current.srcObject.getAudioTracks()[0];
      if (localAudioTrack) {
        localAudioTrack.stop();
      }
    }

    ringingRef.current.pause(); // Stop the ringing tone

    try {
      const callEntryData = {
        AppointmentID: appointmentId,
        CallType: "Incoming Video Call",
        IsCalling: "",
        SenderEmail: senderEmail,
      };
      await update(ref(database, `DoctorCall/${email}`), callEntryData);

      const doctorStatusUpdate = {
        status: "online",
      };
      await update(ref(database, `Doctors/${email}`), doctorStatusUpdate);

      // Remove package if one-time consultation and remote user published
      if (remoteUserPublished) {
        const sessionUser = JSON.parse(sessionStorage.getItem("user"));
        const userPackage = sessionUser?.payLoad?.user?.userPackages;
        if (userPackage && userPackage.VideoCalls === 1) {
          delete sessionUser.payLoad.user.userPackages;
          sessionStorage.setItem("user", JSON.stringify(sessionUser));
        }
        sessionStorage.setItem("redirectFromVideoCall", "true");
      }

      // window.location.href = `/`;
      navigateToFeedback();
    } catch (error) {
      console.error("Error making call:", error);
      // Handle error
    }
  }, [senderEmail, remoteUserPublished]);

  const token = "f0924810ffd04733b7a726cb961157cd";

  useEffect(() => {
    if (!token || !cname || !appointmentId) {
      // Wait until both token and cname are set properly
      return;
    }
    console.log("THis is the doctor channel", cname);
    const agoraClient = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
    setClient(agoraClient);

    // Subscribe to remote user's audio and video tracks
    agoraClient.on("user-published", async (user, mediaType) => {
      await agoraClient.subscribe(user, mediaType);
      console.log("User subscribed:", user);
      setRinging(false);
      setRemoteUserPublished(true); // Set flag when remote user publishes
      if (mediaType === "video") {
        const remoteVideoTrack = user.videoTrack;
        remoteVideoTrack.play(remoteVideoRef.current);
      } else if (mediaType === "audio") {
        const remoteAudioTrack = user.audioTrack;
        remoteAudioTrack.play();
      }
      ringingRef.current.pause();
      clearTimeout(ringTimeoutRef.current);
    });

    agoraClient.on("user-left", (user) => {
      // Remove package if one-time consultation and remote user published
      if (remoteUserPublished) {
        const sessionUser = JSON.parse(sessionStorage.getItem("user"));
        const userPackage = sessionUser?.payLoad?.user?.userPackages;
        if (userPackage && userPackage.VideoCalls === 1) {
          delete sessionUser.payLoad.user.userPackages;
          sessionStorage.setItem("user", JSON.stringify(sessionUser));
        }
        sessionStorage.setItem("redirectFromVideoCall", "true");
      }
      console.log("User unpublished:", user);
      // window.location.href = `/`;
      navigateToFeedback();
    });

    const joinChannel = async () => {
      try {
        console.log("Doctor Channel Name:", cname);
        await agoraClient.join(token, cname, null, null);
        console.log("Successfully joined the channel");

        const localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack();
        const localVideoTrack = await AgoraRTC.createCameraVideoTrack();
        localVideoTrack.play(localVideoRef.current);
        await agoraClient.publish([localAudioTrack, localVideoTrack]);

        // Play the ringing tone
        ringingRef.current.loop = true;
        ringingRef.current.play();
        // alert('ringing')

        // Set a timer to end the call after 120 seconds if not answered
        ringTimeoutRef.current = setTimeout(() => {
          clickVideoCall();
        }, 120000);
      } catch (error) {
        console.error("Failed to join the channel:", error);
      }
    };

    const makeCallEntryForFirebase = async (email, calling, status) => {
      try {
        const callEntryData = {
          AppointmentID: appointmentId,
          CallType: "Incoming Video Call",
          IsCalling: calling,
          CallingPlatform: "WebDoc Site",
          SenderEmail: senderEmail,
        };
        await update(ref(database, `DoctorCall/${email}`), callEntryData);

        const doctorStatusUpdate = {
          status: status,
        };
        await update(ref(database, `Doctors/${email}`), doctorStatusUpdate);
      } catch (error) {
        console.error("Error making call:", error);
        // Handle error
      }
    };

    // Now that cname is available, attempt to join the channel
    joinChannel();
    makeCallEntryForFirebase(email, "true", "busy");
  }, [token, cname]); // Only re-run when token or cname changes

  const toggleAudioMute = () => {
    if (client) {
      const localAudioTrack = client.localTracks.find(
        (track) => track.trackMediaType === "audio"
      );
      if (localAudioTrack) {
        if (audioMuted) {
          localAudioTrack.setEnabled(true);
        } else {
          localAudioTrack.setEnabled(false);
        }
        setAudioMuted(!audioMuted);
      }
    }
  };

  const toggleVideoMute = () => {
    if (client) {
      const localVideoTrack = client.localTracks.find(
        (track) => track.trackMediaType === "video"
      );
      if (localVideoTrack) {
        if (videoMuted) {
          localVideoTrack.setEnabled(true);
        } else {
          localVideoTrack.setEnabled(false);
        }
        setVideoMuted(!videoMuted);
      }
    }
  };

  // Modal open/close handlers
  const handleOpenModal = () => setShowModal(true);
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedFiles([]);
    setUploadError(null);
  };

  // File input handler
  const handleFileChange = (e) => {
    setSelectedFiles(Array.from(e.target.files));
  };

  // File preview helper
  const renderFilePreview = () => {
    if (!selectedFiles.length) return null;
    return (
      <div className="d-flex flex-wrap gap-3 mt-3">
        {selectedFiles.map((file, idx) => {
          const isImage = file.type.startsWith("image/");
          const isPdf = file.type === "application/pdf";
          if (isImage) {
            return (
              <div key={idx} className="text-center">
                <img
                  src={URL.createObjectURL(file)}
                  alt={file.name}
                  style={{
                    width: 80,
                    height: 80,
                    objectFit: "cover",
                    borderRadius: 8,
                    border: "1px solid #ddd",
                  }}
                />
                <div style={{ fontSize: 12, marginTop: 4 }}>{file.name}</div>
              </div>
            );
          } else if (isPdf) {
            return (
              <div key={idx} className="d-flex flex-column align-items-center">
                <span style={{ fontSize: 40, color: "#d9534f" }}>
                  <i className="fa fa-file-pdf-o" aria-hidden="true"></i>
                </span>
                <div
                  style={{
                    fontSize: 12,
                    marginTop: 4,
                    maxWidth: 80,
                    wordBreak: "break-all",
                  }}
                >
                  {file.name}
                </div>
              </div>
            );
          } else {
            return (
              <div key={idx} className="d-flex flex-column align-items-center">
                <span style={{ fontSize: 40, color: "#888" }}>
                  <i className="fa fa-file" aria-hidden="true"></i>
                </span>
                <div
                  style={{
                    fontSize: 12,
                    marginTop: 4,
                    maxWidth: 80,
                    wordBreak: "break-all",
                  }}
                >
                  {file.name}
                </div>
              </div>
            );
          }
        })}
      </div>
    );
  };

  // Upload handler
  const handleUpload = async () => {
    setUploading(true);
    setUploadError(null);
    const formData = new FormData();
    formData.append("doctor_email", email);
    selectedFiles.forEach((file, idx) => {
      formData.append(`files[${idx}]`, file);
    });

    try {
      const response = await fetch(
        "https://digital.webdoc.com.pk/ci4webdocsite/public/files/upload",
        {
          method: "POST",
          body: formData,
        }
      );
      if (!response.ok) throw new Error("Upload failed");
      // Optionally handle response
      handleCloseModal();
    } catch (err) {
      setUploadError("Failed to upload files.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <div className="video-call-container position-fixed">
        <video
          ref={remoteVideoRef}
          className="video-container"
          autoPlay
          playsInline
          muted
        />
        <video
          ref={localVideoRef}
          className="position-absolute top-0 end-0 m-3"
          style={{ width: "200px", height: "150px" }}
          autoPlay
          playsInline
        />

        {ringing && (
          <div className="position-absolute top-50 start-50 bottom-0 translate-middle-x d-flex justify-content-center">
            <p style={{ color: "#00695C" }}>Ringing...</p>
          </div>
        )}

        <div className="position-absolute bottom-0 start-50 translate-middle-x mb-5 d-flex justify-content-center">
          <a
            className="callBtn bg-info me-2 btn-round"
            onClick={toggleAudioMute}
          >
            <FontAwesomeIcon
              icon={audioMuted ? faMicrophoneSlash : faMicrophone}
            />
          </a>
          <a
            className="callBtn bg-danger me-2 btn-round"
            onClick={clickVideoCall}
          >
            <FontAwesomeIcon icon={faPhone} />
          </a>
          <a
            className="callBtn bg-success me-2 btn-round"
            onClick={toggleVideoMute}
          >
            <FontAwesomeIcon icon={videoMuted ? faVideoSlash : faVideo} />
          </a>
          <a
            className="callBtn bg-secondary me-2 btn-round"
            onClick={handleOpenModal}
          >
            <FontAwesomeIcon icon={faPaperclip} />
          </a>
        </div>
      </div>

      {/* Modal for file upload */}
      {showModal && (
        <div
          className="modal fade show"
          style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: 16 }}>
              <div
                className="modal-header"
                style={{ borderBottom: "1px solid #eee" }}
              >
                <h5 className="modal-title" style={{ fontWeight: 600 }}>
                  Upload Attachment
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label" style={{ fontWeight: 500 }}>
                    Select images or PDFs to upload
                  </label>
                  <input
                    type="file"
                    multiple
                    accept="image/*,application/pdf"
                    onChange={handleFileChange}
                    className="form-control"
                  />
                </div>
                {renderFilePreview()}
                {uploadError && (
                  <div className="alert alert-danger mt-2">{uploadError}</div>
                )}
              </div>
              <div
                className="modal-footer"
                style={{ borderTop: "1px solid #eee" }}
              >
                <button
                  className="btn btn-secondary"
                  onClick={handleCloseModal}
                  disabled={uploading}
                >
                  Close
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleUpload}
                  disabled={uploading || selectedFiles.length === 0}
                >
                  {uploading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Uploading...
                    </>
                  ) : (
                    "Send"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VideoCall;
